import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { updateUserById, fetchOrders } from '../../store';
import '../../pages/UserProfilePage.css';
import { isMandatoryProfileComplete, isMandatoryProfileGateRole } from '../../utils/profileCompletion';
import { GENDER_SELECT_OPTIONS } from '../../constants/gender';

const UserProfileContent = ({ profileCompletionLocked = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user);
    // Get orders for the current user from the store
    const allOrders = useSelector((state) => state.orders?.items || []);

    const userId = user?._id || user?.id;
    // Filter orders for this user (if userId exists)
    const orders = userId ? allOrders.filter(order => order.user === userId) : allOrders;
    const wishlist = useSelector((state) => state.wishlist?.items || []);

    useEffect(() => {
        if (userId && user && !user.loading && isMandatoryProfileGateRole(user)) {
            dispatch(fetchOrders(userId));
        }
    }, [userId, user?.role, user.loading, dispatch]);

    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (profileCompletionLocked) setIsEditing(true);
    }, [profileCompletionLocked]);
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    };
    const [formState, setFormState] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        dob: formatDateForInput(user?.dob), // <-- format for input
        gender: user?.gender ?? '',
    });

    // userInfo loads async; initial useState ran before Redux had API fields
    useEffect(() => {
        if (isEditing) return;
        setFormState({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
            phone: user?.phone || '',
            dob: formatDateForInput(user?.dob),
            gender: user?.gender ?? '',
        });
    }, [
        isEditing,
        user?.firstName,
        user?.lastName,
        user?.email,
        user?.phone,
        user?.dob,
        user?.gender,
    ]);

    if (!user) {
        return <div className="user-profile">No user info available.</div>;
    }

    const validateForm = () => {
        const candidate = {
            firstName: formState.firstName,
            lastName: formState.lastName,
            email: formState.email,
            phone: formState.phone,
            dob: formState.dob || null,
        };
        if (!isMandatoryProfileComplete(candidate)) {
            alert('Please enter first name, last name, email, phone (at least 7 digits), and date of birth.');
            return false;
        }
        return true;
    };

    const handleEditClick = async () => {
        if (isEditing) {
            if (!validateForm()) return;
            try {
                const updated = await dispatch(updateUserById({
                    userId: user.userId,
                    data: { ...user, ...formState, gender: formState.gender || null }
                })).unwrap();
                setIsEditing(false);
                if (isMandatoryProfileComplete(updated)) {
                    navigate('/', { replace: true });
                }
            } catch {
                return;
            }
            return;
        }
        setIsEditing(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
    };



    return (
        <main className="profile-content">
            {profileCompletionLocked && (
                <div className="profile-completion-banner" role="status">
                    <strong>Complete your profile to continue.</strong>{' '}
                    First name, last name, email, phone, and date of birth are required before you can shop or browse the store.
                </div>
            )}
            <section className="profile-section">
                <div className="user-section-header">
                    <div>
                        <h2 className="user-section-title">Account Overview</h2>
                        <p className="user-section-subtitle">Your account statistics and recent activity</p>
                    </div>
                </div>
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-number">{orders.length}</div>
                        <div className="stat-label">Total Orders</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">₹ {orders.reduce((sum, o) => sum + (o.totals?.grandTotal || 0), 0).toLocaleString()}</div>
                        <div className="stat-label">Total Spent</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{wishlist.length}</div>
                        <div className="stat-label">Wishlist Items</div>
                    </div>
                </div>
            </section>

            <section className="profile-section">
                <div className="user-section-header">
                    <div>
                        <h2 className="user-section-title">Personal Information</h2>
                        <p className="user-section-subtitle">Update your personal details and contact information</p>
                    </div>
                    <button
                        className="edit-button"
                        style={{
                            background: isEditing
                                ? 'linear-gradient(135deg, #2c5530, #4a7c59)'
                                : 'linear-gradient(135deg, rgba(44, 85, 48, 0.1), rgba(74, 124, 89, 0.15))',
                            color: isEditing ? 'white' : '#2c5530',
                        }}
                        onClick={handleEditClick}
                    >
                        {isEditing ? 'Save Changes' : 'Edit Profile'}
                    </button>
                </div>
                <div className="form-grid">
                    <div className="form-group">
                        <label className="form-label" htmlFor="firstName">First Name <span aria-hidden="true">*</span></label>
                        <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            className="form-input"
                            value={formState.firstName}
                            onChange={handleInputChange}
                            readOnly={!isEditing}
                            style={{
                                backgroundColor: isEditing ? 'white' : '#f9fafb',
                                borderColor: isEditing ? '#4a7c59' : '#d1d5db',
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="lastName">Last Name <span aria-hidden="true">*</span></label>
                        <input
                            id="lastName"
                            name="lastName"
                            type="text"
                            className="form-input"
                            value={formState.lastName}
                            onChange={handleInputChange}
                            readOnly={!isEditing}
                            style={{
                                backgroundColor: isEditing ? 'white' : '#f9fafb',
                                borderColor: isEditing ? '#4a7c59' : '#d1d5db',
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="email">Email <span aria-hidden="true">*</span></label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            className="form-input"
                            value={formState.email}
                            onChange={handleInputChange}
                            readOnly={!isEditing}
                            style={{
                                backgroundColor: isEditing ? 'white' : '#f9fafb',
                                borderColor: isEditing ? '#4a7c59' : '#d1d5db',
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="phone">Phone <span aria-hidden="true">*</span></label>
                        <input
                            id="phone"
                            name="phone"
                            type="tel"
                            className="form-input"
                            value={formState.phone}
                            onChange={handleInputChange}
                            readOnly={!isEditing}
                            style={{
                                backgroundColor: isEditing ? 'white' : '#f9fafb',
                                borderColor: isEditing ? '#4a7c59' : '#d1d5db',
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="dob">Date of Birth <span aria-hidden="true">*</span></label>
                        <input
                            id="dob"
                            name="dob"
                            type="date"
                            className="form-input"
                            value={formState.dob}
                            onChange={handleInputChange}
                            readOnly={!isEditing}
                            style={{
                                backgroundColor: isEditing ? 'white' : '#f9fafb',
                                borderColor: isEditing ? '#4a7c59' : '#d1d5db',
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="gender">Gender <span className="form-optional">(optional)</span></label>
                        <select
                            id="gender"
                            name="gender"
                            className="form-input"
                            value={formState.gender === null || formState.gender === undefined ? '' : formState.gender}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            style={{
                                backgroundColor: isEditing ? 'white' : '#f9fafb',
                                borderColor: isEditing ? '#4a7c59' : '#d1d5db',
                                cursor: isEditing ? 'pointer' : 'default',
                            }}
                        >
                            {GENDER_SELECT_OPTIONS.map(({ value, label }) => (
                                <option key={value || 'unset'} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            {/* <section className="profile-section">
                <div className="user-section-header">
                    <div>
                        <h2 className="user-section-title">Recent Orders</h2>
                        <p className="user-section-subtitle">Your latest paper bag purchases</p>
                    </div>
                </div>
                {orders.slice(0, 3).map((order, idx) => (
                    <div className="order-item" key={order.orderId || idx}>


                        <div className="order-image">
                            {order.items && order.items[0]?.product?.images[0] ? (
                                <img src={order.items[0]?.product && order.items[0]?.product?.images[0]} alt={order.items && order.items[0]?.product?.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                            ) : (
                                <span className="user-profile-image-placeholder">No image</span>
                            )}
                        </div>
                        <div className="order-details">
                            <div className="order-name">{order.items && order.items[0]?.product?.name || 'Order #' + (order.orderId || idx + 1)}</div>
                            <div className="order-date">Ordered on {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}</div>
                        </div>
                        <div className="order-status">{order.status || '-'}</div>
                        <div className="order-price">${order.totalAmount?.toFixed(2) || '0.00'}</div>
                    </div>
                ))}
                <div className="action-buttons">
                    <button className="btn-primary">View All Orders</button>
                    <button className="btn-secondary">Download Invoice</button>
                </div>
            </section> */}
        </main>
    );
};

export default UserProfileContent;
